import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      // Get reCAPTCHA token if available
      let token = null;
      if (executeRecaptcha) {
        token = await executeRecaptcha('contact_form');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const payload = { ...formData };
      if (token) {
        payload.captchaToken = token;
      }
      const res = await axios.post(`${apiUrl}/api/contact`, payload);
      // if backend returns validation errors in body, capture them
      if (res && res.status === 200) {
        setStatus({ type: "success", message: t("contact.success") });
      } else {
        setStatus({ type: "error", message: t("contact.error") });
      }
      setStatus({
        type: "success",
        message: t("contact.success"),
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      // log full error for debugging
      console.error("Contact POST error:", error);
      // If backend returns validation errors (400) or message, show them
      const serverMessage = error?.response?.data;
      let message = t("contact.error");
      if (serverMessage) {
        // If response is object of field errors, join them
        if (typeof serverMessage === "object") {
          message = Object.values(serverMessage).join(" \n ");
        } else if (typeof serverMessage === "string") {
          message = serverMessage;
        }
      }
      setStatus({ type: "error", message });
    } finally {
      setLoading(false);
    }
  };

  const isArabic = i18n.language === "ar";

  return (
    <div
      dir={isArabic ? "rtl" : "ltr"}
      className="min-h-screen bg-[#fafcf0] flex flex-col items-center pt-24 pb-16 px-4"
    >
      <div className="max-w-3xl w-full bg-white rounded-3xl shadow-lg p-8 md:p-12 border border-yellow-200">
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-8 text-[#2d2d2b]">
          {t("contact.title")}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-[#2d2d2b] mb-2">
              {t("contact.name")}
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder={t("contact.namePlaceholder")}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-[#2d2d2b] mb-2">
              {t("contact.email")}
            </label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder={t("contact.emailPlaceholder")}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-[#2d2d2b] mb-2">
              {t("contact.phone")}
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              className={`w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400 ${isArabic ? 'text-right' : 'text-left'}`}
              placeholder={t("contact.phonePlaceholder")}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-[#2d2d2b] mb-2">
              {t("contact.message")}
            </label>
            <textarea
              name="message"
              required
              rows="5"
              value={formData.message}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none"
              placeholder={t("contact.messagePlaceholder")}
            ></textarea>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 font-semibold text-lg rounded-xl transition-all duration-300 ${loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-yellow-400 hover:bg-yellow-500 text-[#2d2d2b]"
              }`}
          >
            {loading ? t("contact.sending") : t("contact.send")}
          </button>

          {/* Status Message */}
          {status.message && (
            <p
              className={`text-center mt-4 font-medium ${status.type === "success" ? "text-green-600" : "text-red-600"
                }`}
            >
              {status.message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
